const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;

const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
});

/**
 * Create gateway app for tenant (one-time setup).
 * Regular user: tenant inferred from backend
 * Superuser: may pass tenant_id
 */
export const createGatewayApp = async (payload) => {
    try {
        const url = new URL(`${API_BASE_URL}/accounts/gateway/create-app/`);
        const response = await fetch(url, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        return {
            success: response.ok,
            data: response.ok ? data : null,
            statusCode: response.status,
            error: response.ok ? null : (data.detail || 'Gateway app creation failed'),
        };
    } catch (error) {
        console.error('Error creating gateway app', error);
        return {
            success: false,
            data: null,
            statusCode: 500,
            error: error.message,
        };
    }
};

/**
 * Issue registration link via gateway.
 * Payload example: { ttl_minutes: 15, max_uses: 1 }
 */
export const issueGatewayRegistrationLink = async (payload = {}) => {
    try {
        const url = new URL(`${API_BASE_URL}/accounts/gateway/issue-reg-link/`);
        const response = await fetch(url, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        return {
            success: response.ok,
            data: response.ok ? data : null,
            statusCode: response.status,
            error: response.ok ? null : (data.detail || 'Failed to issue registration link'),
        };
    } catch (error) {
        console.error('Error issuing registration link', error);
        return {
            success: false,
            data: null,
            statusCode: 500,
            error: error.message,
        };
    }
};

/**
 * List registration links for current tenant (or specified tenant by superuser).
 * Payload example: { include_inactive: false }
 * (superuser may also pass tenant_id / tenant_slug depending on your backend resolver)
 */
export const listGatewayRegistrationLinks = async (payload = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/accounts/gateway/list-reg-links/`);
    const response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    return {
      success: response.ok,
      data: response.ok ? data : null,
      statusCode: response.status,
      error: response.ok ? null : (data.detail || 'Failed to list registration links'),
    };
  } catch (error) {
    console.error('Error listing registration links', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};

/**
 * Invalidate (disable) a registration link by token.
 * Payload example: { token: "..." }
 */
export const invalidateGatewayRegistrationLink = async (payload) => {
  try {
    const url = new URL(`${API_BASE_URL}/accounts/gateway/invalidate-reg-link/`);
    const response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    return {
      success: response.ok,
      data: response.ok ? data : null,
      statusCode: response.status,
      error: response.ok ? null : (data.detail || 'Failed to invalidate registration link'),
    };
  } catch (error) {
    console.error('Error invalidating registration link', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};

/**
 * Send registration link/token via email.
 * Payload example:
 * {
 *   tenant_id: 123,                 // optional for non-superuser
 *   tenant_slug: "acme",            // optional alternative
 *   email: "user@example.com",
 *   token: "....",
 *   recipient_name: "Ján Mrkvička"  // optional
 * }
 */
export const sendGatewayRegistrationLinkEmail = async (payload = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/accounts/gateway/send-reg-link-email/`);
    const response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    return {
      success: response.ok,
      data: response.ok ? data : null,
      statusCode: response.status,
      error: response.ok ? null : (data.detail || 'Failed to send registration email'),
    };
  } catch (error) {
    console.error('Error sending registration email', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};
